import PhotoSphere from "./PhotoSphere";
import PhotoCylinder from "./PhotoCylinder";
import PhotoCube from "./PhotoCube";
import * as TWEEN from "@tweenjs/tween.js";
import Scene from "./Scene";

/**
 * Represents a 360 scene within the player
 */
export default class Scene360 extends Scene {
  /**
   * Takes an image config object and creates the initial 360 scene
   *
   * @example
   * {
   *   url: '/example.jpg',
   *   hotspots: [
   *     {
   *       u: 0,
   *       v: 0.5,
   *       type: 'web',
   *       options: {
   *         url : 'http://google.com'
   *       }
   *     }
   *   ]
   * }
   *
   */
  constructor(player, image, onFullyLoaded = () => {}) {
    super(
      player,
      image,
      _this => {
        // Load photosphere
        _this.initPhotoHolder(image, _this, player);
        _this.group.add(_this._photoHolder);
        _this.setRotation();
      },
      onFullyLoaded,
    );
    this.SCENE_NAME = "Scene360";
    this._player = player;
  }

  /**
   *
   * @param image
   * @param _elem
   * @param player
   */
  initPhotoHolder(image, _elem, player) {
    let __psClass;
    let __urls = [];
    let _cubesEnabled = true;

    if (window.location.search.indexOf("cube_off=true") > -1) {
      _cubesEnabled = false;
    }

    if (_cubesEnabled && (image.cube ?? false)) {
      // cube mode
      __psClass = PhotoCube;
      __urls = {
        fov: this.getSceneFov(),
        urls: image.urls,
        cube: {
          faces: image.cube.faces,
          width: image.cube.width,
        },
      };
      this.isCube = true;
      // eslint-disable-next-line eqeqeq
    } else if (this.getAspectRatio() == 2) {
      __psClass = PhotoSphere;
      __urls = {
        fov: this.getSceneFov(),
        urls: image.urls,
      };
    } else {
      __psClass = PhotoCylinder;
      __urls = {
        fov: this.getSceneFov(),
        urls: image.urls,
      };
    }

    _elem._photoHolder = new __psClass(
      player,
      __urls,
      (texture, full, res) => {
        // Called when photosphere loads
        if (full) {
          player?.onEndImageLoad?.();
        }
        _elem.imageLoaded = true;
      },
      promise => {
        _elem.loadedPromise = promise;
      },
    );
  }

  async loadFullRes(callback = () => {}) {
    await this._photoHolder.loadImage(false, (full, res) => {
      // console.log({ full: full, res: res });
      // console.trace();
      if (res >= 1) {
        if (this.onFullyLoaded) {
          this.onFullyLoaded();
        }
      }
      callback(res >= 1);
    });
    this._player?.onEndImageLoad?.();
  }

  setRotation() {
    this.group.rotation.y = -Math.PI / 2;

    // rotation is not present for jest for some reason
    this._photoHolder.rotation ??= {};

    if (this.isCube) {
      // let v1 = new Vector3(this._image.horizonOffsetX ?? 0, 0, this._image.horizonOffsetZ ?? 0);

      // if (this._image.horizonOffsetX) {
      //   this._photoHolder.rotation.x =
      //     (-180 - this._image.horizonOffsetX) * (Math.PI / 180);
      // }

      this._photoHolder.rotation.z =
        (-180 - (this._image.horizonOffsetZ ?? 0)) * (Math.PI / 180);

      this._photoHolder.rotation.x =
        (-180 - (this._image.horizonOffsetX ?? 0)) * (Math.PI / 180);

      // if (this._image.horizonOffsetZ) {
      //   this._photoHolder.rotation.z =
      //       (-180 - this._image.horizonOffsetZ) * (Math.PI / 180);
      // }

      //   if (this._image.horizonOffsetX || this._image.horizonOffsetZ) {
      //     // console.log("IS CUBE");
      //     // let xOff = this._image.horizonOffsetX ? (-180 - this._image.horizonOffsetX) * (Math.PI / 180) : 0;
      //     // let zOff = this._image.horizonOffsetZ ? (-180 - this._image.horizonOffsetZ) * (Math.PI / 180) : 0;
      //     // let v1 = new Vector3(xOff, 0, zOff);
      //     // let axis = new Vector3( 0, 1, 0 );
      //     // let angle = 90 * (Math.PI / 180);
      //     //
      //     // v1.applyAxisAngle( axis, angle );
      //     //
      //     // this._photoHolder.rotation.x = v1.x;
      //     // this._photoHolder.rotation.z = v1.z;
      //     //
      //     // console.log("CUBE:");
      //     // console.log({
      //     //   X: v1.x,
      //     //   Z: v1.z
      //     // });
      //   }
    } else {
      this._photoHolder.rotation.x =
        (-180 - (this._image.horizonOffsetX ?? 0)) * (Math.PI / 180);

      this._photoHolder.rotation.z =
        (-180 - (this._image.horizonOffsetZ ?? 0)) * (Math.PI / 180);
    }

    this._startingRotation = undefined;
    if (this._image.startingRotation) {
      this.group.rotation.y -= this._image.startingRotation.y;
      this._startingRotation = this._image.startingRotation;
    }
  }

  updateHorizonOffset(offsetInDegrees) {
    // Convert to radians and update
    this.setRotation();
    this._photoHolder.rotation.x = (-180 - offsetInDegrees.x) * (Math.PI / 180);
    this._photoHolder.rotation.z = (-180 - offsetInDegrees.z) * (Math.PI / 180);
  }

  startTransition(
    targetPosition,
    transitionSpeed,
    startPosition,
    easing,
    dispose,
  ) {
    this._transitioning = true;
    this.fullyLoaded = false;

    for (let h = 0; h < this._hotspots.length; h++) {
      this._hotspots[h].setHover(false);
    }

    let position = {
      x: this._photoHolder.position.x,
      y: this._photoHolder.position.y,
      z: this._photoHolder.position.z,
    };

    if (startPosition) {
      position = startPosition;
    }

    const tween = new TWEEN.Tween(position).to(targetPosition, transitionSpeed);
    tween.easing(TWEEN.Easing.Quadratic[easing]);
    tween.onUpdate(() => {
      if (!this._photoHolder) {
        console.warn("No photoholder when trying to transition");
        return;
      }
      this._photoHolder.position.x = position.x;
      this._photoHolder.position.y = position.y;
      this._photoHolder.position.z = position.z;
    });

    if (dispose) {
      tween.onComplete(() => {
        try {
          this.dispose();
        } catch (err) {
          console.warn(err);
        }
        this._transitioning = false;
      });
    } else {
      tween.onComplete(() => {
        this._transitioning = false;
        this.loadFullRes();
      });
    }
    tween.easing(TWEEN.Easing.Quadratic[easing]);

    return tween;
  }

  dispose() {
    if (this._photoHolder) {
      this.group.remove(this._photoHolder);
      this._photoHolder.dispose();
      this._photoHolder = undefined;
    }
    super.dispose();
  }
}
